import React from 'react';
import { Target, Eye, Award, Truck, MapPin, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import headerBg from '../assets/Header-blog-V3.jpg';
import SEO from '../components/SEO';

const About = () => {
  const values = [
    {
      icon: Clock,
      title: 'Speed',
      description: 'We understand urgency. Every minute counts, and we deliver with precision timing.',
    },
    {
      icon: Award,
      title: 'Transparency',
      description: 'Clear communication, honest pricing, and real-time tracking keep you informed.',
    },
    {
      icon: MapPin,
      title: 'Safety',
      description: 'Your items are fully insured and handled with utmost care throughout the journey.',
    },
    {
      icon: Truck,
      title: 'Customer Care',
      description: 'Dedicated support team available 24/7 to ensure your complete satisfaction.',
    },
  ];

  const fleet = [
    {
      type: 'Small Vans',
      capacity: 'Up to 500kg',
      description: 'Perfect for single parcel and small package deliveries in urban areas',
    },
    {
      type: 'Large Vans',
      capacity: 'Up to 1,200kg',
      description: 'Ideal for multiple packages and larger items requiring secure transport',
    },
    {
      type: 'Box Trucks',
      capacity: 'Up to 3,500kg',
      description: 'Designed for pallet deliveries and high-volume commercial shipments',
    },
    {
      type: 'Articulated Lorries',
      capacity: 'Up to 26,000kg',
      description: 'For large-scale freight operations and nationwide distribution',
    },
  ];

  const coverage = [
    { region: 'Greater London', description: 'Same-day delivery ,multi stops' },
    { region: 'UK Nationwide', description: 'Next-day delivery and next day delivery and multi stops to all major cities' },
    { region: 'Scotland & Wales', description: 'Reliable service across all regions' },
    { region: 'Storage Facilities', description: 'Secure warehousing across multiple UK locations' },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="About Us - Reliable Courier Service | Idris Express Ltd"
        description="Learn about Idris Express - your trusted courier partner for fast, reliable delivery services across the UK and Europe. Modern fleet, 24/7 support."
        keywords="courier company, logistics company uk, delivery service about, trusted courier, professional delivery"
        canonicalUrl="https://idrisexpressltd.co.uk/about"
      />
      {/* Hero Section */}
      <section
        className="relative h-[400px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${headerBg})`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">About Idris Express Ltd</h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Your trusted partner for fast, reliable courier and logistics services
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Idris Express Ltd was founded with a simple mission: to provide businesses and individuals with
                  courier services they can truly depend on. What started as a local delivery operation has grown into
                  a comprehensive logistics and storage provider serving clients across the UK.
                </p>
                <p>
                  Our success is built on three pillars: reliability, communication, and innovation. We've invested
                  heavily in technology to give our customers complete visibility of their deliveries, from booking to
                  final signature.
                </p>
                <p>
                  Today, we operate a modern fleet of vehicles, secure storage facilities, and employ experienced drivers
                  to ensure seamless service whether you need delivery across town or secure warehousing for your inventory.
                </p>
              </div>
            </div>
            <div className="relative h-96 lg:h-full min-h-[400px]">
              <img
                src="/assets/w.png"
                alt="Delivery professional"
                className="w-full h-full object-cover rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            <Card className="border-none shadow-lg">
              <CardContent className="pt-8">
                <div className="flex items-center mb-4">
                  <Target className="h-10 w-10 text-gray-800 mr-4" />
                  <h3 className="text-2xl font-bold text-gray-900">Our Mission</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  To deliver exceptional courier and logistics services that exceed expectations through reliability,
                  speed, and transparent communication. We strive to be the first choice for businesses and individuals
                  who demand excellence.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="pt-8">
                <div className="flex items-center mb-4">
                  <Eye className="h-10 w-10 text-gray-800 mr-4" />
                  <h3 className="text-2xl font-bold text-gray-900">Our Vision</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  To become the UK's most trusted courier and logistics partner, recognised for innovation,
                  sustainability, and unwavering commitment to customer success. We envision a future where every
                  delivery is seamless and stress-free.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">The principles that guide everything we do</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="p-4 bg-gray-100 rounded-full">
                      <value.icon className="h-8 w-8 text-gray-800" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Coverage Areas</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Extensive network spanning the UK and Europe
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coverage.map((area, index) => (
              <Card key={index} className="border-l-4 border-gray-800 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-6 w-6 text-gray-800 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{area.region}</h3>
                      <p className="text-gray-600 text-sm">{area.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet & Technology */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Fleet & Technology</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Modern vehicles and cutting-edge systems for reliable deliveries
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {fleet.map((vehicle, index) => (
              <Card key={index} className="border-none shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <Truck className="h-8 w-8 text-gray-800 flex-shrink-0 mt-1" />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{vehicle.type}</h3>
                        <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                          {vehicle.capacity}
                        </span>
                      </div>
                      <p className="text-gray-600">{vehicle.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-none shadow-xl bg-gray-800 text-white">
            <CardContent className="pt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4">Advanced Tracking System</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Our proprietary tracking platform provides real-time updates at every stage of delivery. GPS-enabled
                    vehicles, automated notifications, and instant proof of delivery give you complete peace of mind.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4">Intelligent Dispatch</h3>
                  <p className="text-gray-300 leading-relaxed">
                    AI-powered route optimization ensures the fastest, most efficient deliveries while reducing our carbon
                    footprint. Our dispatch system dynamically adapts to traffic, weather, and priority levels.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default About;